"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import type { OnboardingStateResponse, ProfileResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { LoadingState, ErrorState } from "@/components/state"
import { Field } from "@/components/onboarding/Field"
import { OptionGroup } from "@/components/onboarding/OptionGroup"
import { ActivitySection } from "@/components/onboarding/steps/ActivitySection"
import { MoreSubpageHeader } from "@/components/layout/MoreSubpageHeader"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { PlanSyncBanner, usePlanSync } from "@/components/plan/PlanSyncBanner"
import { GroceryBudgetSection } from "@/components/budget/GroceryBudgetSection"
import { formatKcal, useI18n } from "@/i18n"

/**
 * /app/more/preferences — split out of the old combined ProfilePage (owner
 * UX pass 2026-08-28): everything that shapes how plans are GENERATED
 * (cooking, planning mode, snacks, activity) plus the ADR-029 offer-email
 * notification consent, plus (owner request 2026-08-28) the dietary style
 * (step-5 PUT) and cooking time budget (step-9 PUT). ProfilePage now holds
 * only personal information. Budget Model v2 (ADR-052): the grocery budget
 * (soft weekly goal + hard optional maximum checkout, legacy choice card,
 * outlook) lives in its own GroceryBudgetSection with its own PATCH — a
 * budget change never rebuilds the plan.
 *
 * ADR-033: saving plan-shaping changes AUTO-REGENERATES the plan (today +
 * coming week) with an announcing banner — the old snack-only CTA left the
 * plan stale after e.g. a wearable-kcal change dropped the calorie target.
 * The offer-email toggle still saves IMMEDIATELY on change in its own
 * PATCH and never touches the plan.
 */
export default function PreferencesPage() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")
  const [draft, setDraft] = useState<Partial<ProfileResponse>>({})
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const [onboarding, setOnboarding] = useState<OnboardingStateResponse | null>(null)
  const [dietaryDraft, setDietaryDraft] = useState<string | null>(null)
  const [perMealDraft, setPerMealDraft] = useState<number | null>(null)
  const [perDayDraft, setPerDayDraft] = useState<number | null>(null)
  const { state: planSync, start: startPlanSync } = usePlanSync()
  const [offerEmailsState, setOfferEmailsState] = useState<"idle" | "saving" | "saved" | "error">(
    "idle"
  )

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.getProfile(),
      // Dietary style + time budget live in the stored onboarding answers; a
      // failure here degrades those two sections away instead of blocking.
      api.getOnboarding().catch(() => null),
    ])
      .then(([full, ob]) => {
        if (cancelled) return
        setProfile(full)
        setDraft(full)
        setOnboarding(ob)
        const step5 = ob?.answers.find(a => a.step === 5)?.answer as
          { dietaryStyle?: string } | undefined
        const step9 = ob?.answers.find(a => a.step === 9)?.answer as
          { timePerMealMinutes?: number; timePerDayMinutes?: number } | undefined
        setDietaryDraft(step5?.dietaryStyle ?? null)
        setPerMealDraft(step9?.timePerMealMinutes ?? null)
        setPerDayDraft(step9?.timePerDayMinutes ?? null)
        setState("ready")
      })
      .catch(() => {
        if (!cancelled) setState("error")
      })
    return () => {
      cancelled = true
    }
  }, [api])

  async function handleOfferEmailsToggle(optIn: boolean) {
    if (!profile) return
    setOfferEmailsState("saving")
    try {
      const updated = await api.patchProfile({ offerEmailsOptIn: optIn })
      setProfile(updated)
      setDraft(updated)
      setOfferEmailsState("saved")
    } catch {
      setOfferEmailsState("error")
    }
  }

  async function handleSave() {
    if (!profile) return
    setSaveState("saving")
    const prevKcal = profile.calorieTarget.kcal
    const step5Answer = onboarding?.answers.find(a => a.step === 5)?.answer as
      { dietaryStyle?: string } | undefined
    const step9Answer = onboarding?.answers.find(a => a.step === 9)?.answer as
      { timePerMealMinutes?: number; timePerDayMinutes?: number; equipment?: unknown } | undefined
    const dietaryChanged = dietaryDraft !== null && dietaryDraft !== step5Answer?.dietaryStyle
    const timeChanged =
      (perMealDraft !== null && perMealDraft !== step9Answer?.timePerMealMinutes) ||
      (perDayDraft !== null && perDayDraft !== step9Answer?.timePerDayMinutes)
    try {
      // Budget Model v2: the grocery budget has its own section + PATCH
      // (GroceryBudgetSection) and never rides along here; householdServings
      // is not a user setting (ADR-047: household planning inactive).
      const updated = await api.patchProfile({
        cookingEnjoyment: draft.cookingEnjoyment,
        cookingSkill: draft.cookingSkill,
        planningMode: draft.planningMode,
        snackPreference: draft.snackPreference,
        activity: draft.activity,
        preferredRetailers: draft.preferredRetailers,
      })
      // Dietary style (hard rule) and time budget write through the same
      // canonical onboarding steps the wizard used; the server re-derives
      // hard constraints / time budget from them (ConstraintsPage precedent).
      let queuedJobId: string | null = null
      if (dietaryChanged) {
        const stepRes = await api.putOnboardingStep(5, {
          step: 5,
          answer: { dietaryStyle: dietaryDraft },
        } as never)
        // ADR-036: the server queues the regeneration atomically with the
        // constraint change — attach to that job, don't queue a second one.
        queuedJobId =
          (stepRes as { triggeredPlanJobId?: string | null }).triggeredPlanJobId ?? null
      }
      if (timeChanged && step9Answer) {
        await api.putOnboardingStep(9, {
          step: 9,
          answer: {
            timePerMealMinutes: perMealDraft ?? step9Answer.timePerMealMinutes,
            timePerDayMinutes: perDayDraft ?? step9Answer.timePerDayMinutes,
            equipment: step9Answer.equipment ?? "none",
          },
        } as never)
      }
      setProfile(updated)
      setDraft(updated)
      setSaveState("saved")
      // ADR-033: everything on this page shapes plan generation — any actual
      // change rebuilds the plan, announced via the banner (never silent).
      const profileChanged =
        JSON.stringify(updated) !== JSON.stringify(profile) || dietaryChanged || timeChanged
      if (profileChanged) {
        startPlanSync({ fromKcal: prevKcal, toKcal: updated.calorieTarget.kcal, jobId: queuedJobId })
      }
    } catch {
      setSaveState("error")
    }
  }

  if (state === "loading") return <LoadingState />
  if (state === "error" || !profile) return <ErrorState onRetry={() => window.location.reload()} />

  return (
    <section
      aria-labelledby="preferences-heading"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <MoreSubpageHeader
        title={t("moreProfile.profile.preferencesTitle")}
        headingId="preferences-heading"
      />

      <div className="flex flex-col gap-6 rounded-bb-md border border-line bg-paper p-5">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-ink">
            {t("moreProfile.profile.calorieTargetLabel")}
          </span>
          <span className="text-lg text-ink">{formatKcal(profile.calorieTarget.kcal, locale)}</span>
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-ink">
            {t("moreProfile.profile.cookingLabel")}
          </p>
          <OptionGroup
            legend={t("moreProfile.profile.cookingLabel")}
            columns={3}
            value={draft.cookingSkill ?? null}
            onChange={v =>
              setDraft(d => ({ ...d, cookingSkill: v as ProfileResponse["cookingSkill"] }))
            }
            options={[
              { value: "beginner", label: t("onboarding.step8.beginner") },
              { value: "intermediate", label: t("onboarding.step8.intermediate") },
              { value: "advanced", label: t("onboarding.step8.advanced") },
            ]}
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-ink">
            {t("moreProfile.profile.planningModeLabel")}
          </p>
          <OptionGroup
            legend={t("moreProfile.profile.planningModeLabel")}
            columns={3}
            value={draft.planningMode ?? null}
            onChange={v =>
              setDraft(d => ({ ...d, planningMode: v as ProfileResponse["planningMode"] }))
            }
            options={[
              { value: "daily", label: t("onboarding.step10.daily") },
              { value: "batch", label: t("onboarding.step10.batch") },
              { value: "hybrid", label: t("onboarding.step10.hybrid") },
            ]}
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-ink">
            {t("moreProfile.profile.snackLabel")}
          </p>
          <OptionGroup
            legend={t("moreProfile.profile.snackLabel")}
            columns={2}
            value={draft.snackPreference ?? null}
            onChange={v =>
              setDraft(d => ({ ...d, snackPreference: v as ProfileResponse["snackPreference"] }))
            }
            options={[
              { value: "none", label: t("onboarding.step10.snackNone") },
              { value: "morning", label: t("onboarding.step10.snackMorning") },
              { value: "afternoon", label: t("onboarding.step10.snackAfternoon") },
              { value: "both", label: t("onboarding.step10.snackBoth") },
            ]}
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-ink">
            {t("onboarding.step4.activityLevelLabel")}
          </p>
          {/* ADR-027 rule 7: same component as onboarding step 4 — editing
              here re-triggers a server-side target recompute (never
              touching a manual target), same as a goal change; the current
              plan is not invalidated (unlike snack-preference/hard-
              constraint changes above). */}
          <ActivitySection
            value={
              draft.activity ?? {
                level: null,
                sports: [],
                wearableKcalEnabled: false,
                avgDeviceActiveKcal: null,
              }
            }
            onChange={activity => setDraft(d => ({ ...d, activity }))}
          />
        </div>

        {dietaryDraft !== null && (
          <div>
            <p className="mb-1 text-sm font-semibold text-ink">
              {t("moreProfile.profile.dietaryStyleLabel")}
            </p>
            <p className="mb-2 text-xs text-ink-2">{t("moreProfile.profile.dietaryStyleHint")}</p>
            <OptionGroup
              legend={t("moreProfile.profile.dietaryStyleLabel")}
              columns={2}
              value={dietaryDraft}
              onChange={v => setDietaryDraft(v)}
              options={[
                { value: "omnivore", label: t("onboarding.step5.omnivore") },
                { value: "pescatarian", label: t("onboarding.step5.pescatarian") },
                { value: "vegetarian", label: t("onboarding.step5.vegetarian") },
                { value: "vegan", label: t("onboarding.step5.vegan") },
              ]}
            />
          </div>
        )}

        {perMealDraft !== null && perDayDraft !== null && (
          <div>
            <p className="mb-1 text-sm font-semibold text-ink">
              {t("moreProfile.profile.timeBudgetLabel")}
            </p>
            <p className="mb-2 text-xs text-ink-2">{t("moreProfile.profile.timeBudgetHint")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("onboarding.step9.perMealLabel")} htmlFor="prefs-per-meal">
                <Input
                  id="prefs-per-meal"
                  type="number"
                  min={5}
                  max={180}
                  step={5}
                  value={perMealDraft}
                  onChange={e => setPerMealDraft(Math.max(0, Number(e.target.value)))}
                />
              </Field>
              <Field label={t("onboarding.step9.perDayLabel")} htmlFor="prefs-per-day">
                <Input
                  id="prefs-per-day"
                  type="number"
                  min={10}
                  max={600}
                  step={10}
                  value={perDayDraft}
                  onChange={e => setPerDayDraft(Math.max(0, Number(e.target.value)))}
                />
              </Field>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            type="button"
            className="min-h-11"
            onClick={() => void handleSave()}
            disabled={saveState === "saving"}
          >
            {saveState === "saving"
              ? t("onboarding.shell.saving")
              : t("moreProfile.profile.saveButton")}
          </Button>
          {saveState === "saved" && (
            <span className="text-sm text-green">{t("moreProfile.profile.saved")}</span>
          )}
          {saveState === "error" && (
            <span className="text-sm text-destructive">{t("moreProfile.profile.saveError")}</span>
          )}
        </div>
      </div>

      {/* ------------------------------ Grocery budget (Budget Model v2, ADR-052) */}
      <GroceryBudgetSection
        profile={profile}
        onProfileUpdated={updated => {
          setProfile(updated)
          setDraft(updated)
        }}
      />

      {/* ---------------------------------------------- Notifications (ADR-029) */}
      <div className="flex flex-col gap-3 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">
          {t("moreProfile.profile.notificationsTitle")}
        </h2>
        <div className="flex items-start gap-3">
          <input
            id="profile-offer-emails"
            type="checkbox"
            checked={profile.offerEmailsOptIn}
            disabled={offerEmailsState === "saving"}
            onChange={e => void handleOfferEmailsToggle(e.target.checked)}
            className="mt-0.5 size-5 shrink-0 rounded border-line text-green focus-visible:ring-2 focus-visible:ring-green"
          />
          <label htmlFor="profile-offer-emails" className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-ink">
              {t("moreProfile.profile.offerEmailsLabel")}
            </span>
            <span className="text-sm text-ink-2">{t("moreProfile.profile.offerEmailsBody")}</span>
          </label>
        </div>
        {offerEmailsState === "saved" && (
          <span className="text-sm text-green">{t("moreProfile.profile.offerEmailsSaved")}</span>
        )}
        {offerEmailsState === "error" && (
          <span className="text-sm text-destructive">
            {t("moreProfile.profile.offerEmailsError")}
          </span>
        )}
      </div>

      <PlanSyncBanner
        state={planSync}
        onRetry={() =>
          startPlanSync({
            fromKcal: null,
            toKcal: profile.calorieTarget.kcal,
          })
        }
      />
    </section>
  )
}
